import React from 'react';
import styled, { css } from '@emotion/native';
import { TouchableOpacity, View } from 'react-native';
import { black, white, lightGreen, green } from '@/shared/styles/colors';
import ScrollableView from '@/shared/components/ScrollableView';
import { BodyText, H3 } from '@/shared/components/StyledText';
import ReceiptItem, {
  ReceiptItemProps,
  ReceiptLabel,
  ReceiptValue,
} from './ReceiptItem';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { getPaddingHorizontal } from './utils';
import useStore from '@/stores/useStore';
import DashedBorder from './DashedBorder';
import { BOX_SHADOW_STYLE } from '@/shared/styles/constants';
import { ReceiptLabelText } from './constants';

export interface ReceiptProps {
  totalPrice?: string;
  items: ReceiptItemProps[];
  notes?: string;
  /** Replaces the default fine print under the estimated total. */
  finePrint?: string;
  showHelpCenter?: boolean;
  onHelpCenterPress?: () => void;
  isScrollable?: boolean;
}

const Receipt: React.FC<ReceiptProps> = ({
  totalPrice,
  items,
  notes,
  finePrint,
  showHelpCenter = true,
  onHelpCenterPress,
  isScrollable = true,
}) => {
  const { isDesktop } = useBreakpoint();
  const { isStylist } = useStore();
  const Container = isScrollable
    ? ScrollableReceiptContainer
    : ReceiptContainer;

  const subText =
    finePrint ??
    (isStylist
      ? 'Estimated amount is based on app fees, your price tier and call duration.'
      : "There are no initial fees—payment will be processed after your session. Estimated amount is based on selected price tier and call duration. The stylist's rate may vary for scheduled sessions.");

  return (
    <Container>
      {totalPrice && (
        <>
          <ReceiptItem
            label={ReceiptLabelText.TotalPriceEstimated}
            value={totalPrice}
            hideBorder
            customElement={<H3>~{totalPrice}</H3>}
            subText={<FinePrint>{subText}</FinePrint>}
          />
          <View style={{ height: 14 }} />
        </>
      )}
      <DottedBorderBox>
        <DashedBorder />
        {items.map((item) => (
          <ReceiptItem key={item.label} {...item} />
        ))}
        <ReceiptColumn paddingHorizontal={getPaddingHorizontal(isDesktop)}>
          <ReceiptLabel>Client Notes</ReceiptLabel>
          {Boolean(notes) && (
            <ReceiptValue
              style={{
                fontSize: 14,
                maxWidth: '100%',
                textAlign: 'left',
                textTransform: 'none',
              }}
            >
              {notes}
            </ReceiptValue>
          )}
        </ReceiptColumn>
        <DashedBorder />
      </DottedBorderBox>
      {showHelpCenter && (
        <FlexRow>
          <QuestionText>Have questions? </QuestionText>
          <TouchableOpacity onPress={onHelpCenterPress}>
            <LinkText>Visit Help Center</LinkText>
          </TouchableOpacity>
        </FlexRow>
      )}
    </Container>
  );
};

const OuterContainerStyles = css({
  backgroundColor: white,
  width: '100%',
  borderRadius: 8,
  shadowColor: black,
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.1,
  shadowRadius: 4,
  elevation: 2,
});

const ReceiptContainer = styled.View(OuterContainerStyles);

const ScrollableReceiptContainer = styled(ScrollableView)(OuterContainerStyles);

const FinePrint = styled(BodyText)({
  fontSize: 12,
  lineHeight: 14,
  letterSpacing: -0.12,
});

const DottedBorderBox = styled.View({
  position: 'relative',
  backgroundColor: white,
  marginBottom: 85,
  ...BOX_SHADOW_STYLE,
});

const ReceiptColumn = styled.View(
  ({ paddingHorizontal = 26 }: { paddingHorizontal: number }) => ({
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingVertical: 20,
    marginBottom: 20,
    paddingHorizontal,
  }),
);

const QuestionText = styled(BodyText)({
  fontSize: 14,
  color: lightGreen,
  fontWeight: '400',
  marginBottom: 7,
});

const FlexRow = styled.View({
  flexDirection: 'row',
  justifyContent: 'center',
  marginBottom: 36,
});

const LinkText = styled(BodyText)({
  color: green,
  fontWeight: '500',
  textDecorationLine: 'underline',
  fontSize: 14,
});

export default Receipt;
